import { supabase } from '@/lib/supabase';
import { checkVisitorIp } from '@/lib/visitorIp';
import { generateIban } from '@/utils/format';
import { logoutBank, resetBankAttempts } from './useBankAuth';
import { clearCardSession, resetCardAttempts } from './useCardAuth';

const STORAGE_KEY = 'izipay_client_code';

export type ClientAccount = {
  id: string;
  accessCode: string;
  fullName: string;
  email: string;
  iban: string;
  balance: number;
  currency: string;
  status: 'active' | 'suspended';
  memberSince: string;
  maxBankAttempts: number;
  maxCardAttempts: number;
};

export type LoginResult =
  | { ok: true; client: ClientAccount }
  | { ok: false; reason: 'empty' | 'invalid' | 'blocked' };

type ClientRow = {
  id: string;
  access_code: string;
  full_name: string;
  email: string;
  iban: string | null;
  balance: number | string;
  currency: string;
  status: string;
  member_since: string;
  max_bank_attempts: number | string | null;
  max_card_attempts: number | string | null;
};

function mapClient(row: ClientRow): ClientAccount {
  return {
    id: row.id,
    accessCode: row.access_code,
    fullName: row.full_name,
    email: row.email,
    iban: row.iban && row.iban.trim() ? row.iban : generateIban(row.id),
    balance: Number(row.balance),
    currency: row.currency,
    status: row.status === 'suspended' ? 'suspended' : 'active',
    memberSince: row.member_since,
    maxBankAttempts: Number(row.max_bank_attempts ?? 2) || 2,
    maxCardAttempts: Number(row.max_card_attempts ?? 2) || 2,
  };
}

export async function loginClient(code: string): Promise<LoginResult> {
  const trimmed = code.trim();
  if (!trimmed) {
    return { ok: false, reason: 'empty' };
  }

  // Contrôle de blocage basé sur l'adresse IP réelle du visiteur, déterminée
  // côté Backend à partir des en-têtes de la requête.
  const { blocked } = await checkVisitorIp();
  if (blocked) {
    return { ok: false, reason: 'blocked' };
  }

  const { data } = await supabase
    .from('clients')
    .select('*')
    .eq('access_code', trimmed)
    .eq('status', 'active')
    .maybeSingle();

  if (!data) {
    return { ok: false, reason: 'invalid' };
  }

  localStorage.setItem(STORAGE_KEY, (data as ClientRow).access_code);
  // Nouvelle connexion : les compteurs de tentatives (bancaire et carte) sont
  // remis à leur maximum configuré pour ce client et toute session carte
  // précédente est effacée.
  const account = mapClient(data as ClientRow);
  clearCardSession();
  resetBankAttempts(account.maxBankAttempts);
  resetCardAttempts(account.maxCardAttempts);
  return { ok: true, client: account };
}

export async function getSessionClient(): Promise<ClientAccount | null> {
  const code = localStorage.getItem(STORAGE_KEY);
  if (!code) return null;

  const { data } = await supabase
    .from('clients')
    .select('*')
    .eq('access_code', code)
    .maybeSingle();

  return data ? mapClient(data as ClientRow) : null;
}

export function logoutClient(): void {
  localStorage.removeItem(STORAGE_KEY);
  logoutBank();
  clearCardSession();
}